import { MultiLevelSelectItem, MultiLevelSelectOption } from './types';

/**
 * Function to get selected keys based on selectedKeys array.
 * @param {MultiLevelSelectOption[]} options - Array of MultiLevelSelectOption objects.
 * @param {string[]} selectedKeys - Array of selected keys.
 * @returns {string[]} - Array of keys where all items are selected.
 */
export function getSelectedKeysForMultiLevelSelectDropdown(
  options: MultiLevelSelectOption[],
  selectedKeys: string[],
): string[] {
  return options?.reduce((result, option) => {
    const allItemsSelected = option.items?.every((item) =>
      selectedKeys?.includes(item?.key),
    );
    if (allItemsSelected) {
      result.push(option.key);
    }
    return result;
  }, [] as string[]);
}

/**
 * Function to filter dropdown options based on a search string.
 * @param {MultiLevelSelectOption[]} options - Array of MultiLevelSelectOption objects.
 * @param {string} searchString - Search string to match against the `value` property.
 * @returns {MultiLevelSelectOption[]} - Filtered options array.
 */
export function getFilterOptionsForMultiLevelSelectDropdown(
  options: MultiLevelSelectOption[],
  searchString: string,
): MultiLevelSelectOption[] {
  const lowerCasedSearchString = searchString.toLowerCase();

  if (lowerCasedSearchString === '') {
    return options;
  }

  return options
    ?.map((option) => {
      // Filter nested items based on the search string
      const filteredItems = option.items?.filter((item) =>
        item.value?.toLowerCase().includes(lowerCasedSearchString),
      );

      // Check if the parent option matches the search string
      const isParentMatched = option.value
        ?.toLowerCase()
        .includes(lowerCasedSearchString);

      // Return the option if either it matches or has matching children
      if (isParentMatched || filteredItems?.length > 0) {
        return {
          ...option,
          items: filteredItems, // Include only filtered items
        };
      }

      return null;
    })
    ?.filter((option) => option !== null) as MultiLevelSelectOption[];
}

/**
 * Updates the selectedKeys array based on the selection/deselection of a MultiLevelSelectOption.
 * @param {MultiLevelSelectOption} option - The option to select/deselect.
 * @param {string[]} selectedKeys - The current array of selected keys.
 * @returns {string[]} - Updated selectedKeys array.
 */
export function onSelectDeselectMultiLevelDropdownOption(
  option: MultiLevelSelectOption,
  selectedKeys: string[],
): string[] {
  const optionKeys = option?.items?.map((item) => item?.key);

  // Check if all keys of this option are selected
  const allKeysSelected = optionKeys?.every((key) => selectedKeys?.includes(key));

  if (allKeysSelected) {
    // If all keys are selected, remove them
    return selectedKeys?.filter((key) => !optionKeys.includes(key));
  }
  // If partially selected or none selected, select all keys
  const updatedKeys = new Set(selectedKeys);
  optionKeys.forEach((key) => updatedKeys.add(key)); // Automatically avoids duplicates
  return Array.from(updatedKeys);
}

/**
 * Toggles the selection of an individual item.
 * @param {MultiLevelSelectItem} item - The item to select/deselect.
 * @param {string[]} selectedKeys - The current array of selected keys.
 * @returns {string[]} - Updated selectedKeys array.
 */
export function onSelectDeselectMultiLevelDropdownItem(
  item: MultiLevelSelectItem,
  selectedKeys: string[],
): string[] {
  const { key } = item;

  if (selectedKeys?.includes(key)) {
    // If the item is already selected, remove it
    return selectedKeys?.filter((selectedKey) => selectedKey !== key);
  }
  // If the item is not selected, add it
  return [...selectedKeys, key];
}

/**
 * Returns selected items based on the options and selected keys.
 * @param {MultiLevelSelectOption[]} options - Array of options containing items.
 * @param {string[]} selectedKeys - Array of selected keys.
 * @returns {Array<{ key: string, value: string }>} - Array of selected items.
 */
export function getSelectedItemsForMultiLevelSelectDropdown(
  options: MultiLevelSelectOption[],
  selectedKeys: string[],
): { key: string; name: string }[] {
  const selectedItems: { key: string; name: string }[] = [];

  options?.forEach((option) => {
    option.items?.forEach((item) => {
      if (selectedKeys?.includes(item.key)) {
        selectedItems?.push({ key: item.key, name: item.value });
      }
    });
  });

  return selectedItems;
}
